import { useState, type FormEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Search, UserPlus } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventTheme {
  id: number
  name: string
  about: string | null
  ended: boolean
  primaryColor: string
  secondaryColor: string
  font: string | null
  bannerUrl: string | null
  logoUrl: string | null
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: EventTheme
  urls: AppShellProps['urls'] & {
    search: string
    register: string
  }
  translations: AppShellProps['translations'] & {
    welcome: string
    registered: string
    notRegistered: string
    searchPlaceholder: string
    search: string
    newRegistration: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function SelfRegister(props: Props) {
  const { event, urls, translations: t, ...shell } = props
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)

  const handleSearch = (e: FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return
    setSearching(true)
    router.get(urls.search, { q: query.trim() }, {
      preserveState: false,
      onFinish: () => setSearching(false),
    })
  }

  const themeStyle: React.CSSProperties = {
    '--ev-primary': event.primaryColor,
    '--ev-secondary': event.secondaryColor,
    fontFamily: event.font || undefined,
  } as React.CSSProperties

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={`${t.welcome} – ${event.name}`} />

      <div className="min-h-screen bg-[#F7F7F5]" style={themeStyle}>
        {/* Banner / Logo */}
        {event.bannerUrl ? (
          <div className="w-full max-h-64 overflow-hidden">
            <img src={event.bannerUrl} alt={event.name} className="w-full object-cover" />
          </div>
        ) : event.logoUrl ? (
          <div className="flex justify-center pt-10">
            <img src={event.logoUrl} alt={event.name} className="h-24 object-contain" />
          </div>
        ) : null}

        <div className="mx-auto max-w-md px-4 py-12">
          {/* Event name */}
          <h1
            className="mb-8 text-center text-2xl font-semibold"
            style={{ color: event.primaryColor }}
          >
            {event.name}
          </h1>

          {/* Search form */}
          <form onSubmit={handleSearch} className="space-y-4">
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="h-12 text-base"
              dir="auto"
            />

            <Button
              type="submit"
              disabled={searching || !query.trim()}
              className="w-full h-12 text-base font-medium text-white"
              style={{ backgroundColor: event.primaryColor }}
            >
              <Search className="mr-2 h-4 w-4" />
              {t.search}
            </Button>
          </form>

          {/* Divider */}
          <div className="my-8 flex items-center gap-3">
            <div className="flex-1 border-t border-[#E8E8E5]" />
            <span className="text-sm text-[#91918E]">{t.notRegistered}</span>
            <div className="flex-1 border-t border-[#E8E8E5]" />
          </div>

          {/* New registration link */}
          <Button
            variant="outline"
            className="w-full h-12 text-base"
            onClick={() => router.visit(urls.register)}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {t.newRegistration}
          </Button>
        </div>
      </div>
    </AppShell>
  )
}
